import type { CertSummary } from "../lib/parse";
import { Mono } from "./CopyButton";

export function CertChainPanel({
  certs,
  signerIndex,
  fingerprints,
  genTime,
}: {
  certs: CertSummary[];
  signerIndex: number | null;
  fingerprints: string[];
  genTime: string | null;
}) {
  if (certs.length === 0) {
    return (
      <section className="panel">
        <h3>Certificates</h3>
        <p className="muted">
          No certificates are embedded in this token (certReq was false at
          request time). The signer chain must be obtained out of band.
        </p>
      </section>
    );
  }
  const gen = genTime ? new Date(genTime) : null;

  return (
    <section className="panel">
      <h3>Certificates</h3>
      <div className="cert-cards">
        {certs.map((cert, i) => {
          const validAtGen =
            gen &&
            new Date(cert.notBefore) <= gen &&
            new Date(cert.notAfter) >= gen;
          const expiredNow = new Date(cert.notAfter) < new Date();
          return (
            <details key={cert.serialHex + i} className="cert-card">
              <summary>
                <span className="cert-subject">{cert.subject}</span>
                <span className="cert-badges">
                  {i === signerIndex && <span className="badge badge-signer">signer</span>}
                  {gen && (
                    <span
                      className={`badge ${validAtGen ? "badge-ok" : "badge-bad"}`}
                      title="Certificate validity evaluated at the token's genTime"
                    >
                      {validAtGen ? "valid at genTime" : "NOT valid at genTime"}
                    </span>
                  )}
                  {expiredNow && validAtGen && (
                    <span className="badge badge-note" title="Expired since — timestamps outlive certificates by design">
                      expired since
                    </span>
                  )}
                  {i === signerIndex && !(cert.isTimeStampingEku && cert.ekuCritical) && (
                    <span className="badge badge-bad">
                      missing critical timeStamping EKU
                    </span>
                  )}
                </span>
              </summary>
              <dl className="kv">
                <dt>Issuer</dt>
                <dd>{cert.issuer}</dd>
                <dt>Serial</dt>
                <dd>
                  <Mono>{cert.serialHex}</Mono>
                </dd>
                <dt>Valid</dt>
                <dd>
                  {cert.notBefore.slice(0, 10)} → {cert.notAfter.slice(0, 10)}
                </dd>
                <dt>Key</dt>
                <dd>{cert.keyAlgorithm}</dd>
                {fingerprints[i] && (
                  <>
                    <dt>SHA-256</dt>
                    <dd>
                      <Mono>{fingerprints[i]}</Mono>
                    </dd>
                  </>
                )}
              </dl>
            </details>
          );
        })}
      </div>
    </section>
  );
}
